import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import type { HttpErrorResponse } from '@angular/common/http';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroDocumentText,
  heroLockClosed,
  heroMicrophone,
  heroMusicalNote,
  heroPaperAirplane,
  heroPaperClip,
  heroPencilSquare,
  heroPhoto,
  heroSparkles,
  heroStop,
  heroVideoCamera,
  heroXMark,
} from '@ng-icons/heroicons/outline';
import { IfPermissionDirective } from '../../../../core/auth/permission.directive';
import { ToastrService, UiBadge, UiButton, UiFileInput, UiIconButton, UiPanelCard, UiTextarea } from '../../../../shared/ui';
import { ConversationsStore } from '../../data/conversations.store';
import { quotedReplySenderLabel } from '../../i18n/conversation-labels';
import type { ThreadMessage } from '../../models/conversation-view.types';
import { FileUploadService } from '../../media/file-upload.service';
import { channelKindFromLabel, exceedsSizeLimit, maxUploadBytes } from '../../media/media-constraints';
import { contentTypeToMediaKind, formatBytes, type MediaKind } from '../../media/media-kind';
import { VoiceRecorderService } from '../../media/voice-recorder.service';
import { AudioPlayerComponent } from './audio-player';
import { TemplatePickerDialogComponent, type TemplatePickerSubmit } from './template-picker-dialog';

/** Where the pending attachment came from — distinguishes voice-note recordings (rendered
 * with an inline audio preview) from regular paperclip uploads (rendered as a file chip). */
export type PendingAttachmentSource = 'voice' | 'file';

/** Local lifecycle of a queued attachment's upload — starts immediately on attach (not deferred
 *  to send), so a rejected format surfaces right away, inline on the chip. */
export type PendingAttachmentStatus = 'uploading' | 'uploaded' | 'error';

/** A file queued in the composer. Upload starts as soon as it's attached; send() only fires for
 *  items that finished uploading (`status: 'uploaded'`) — an item stuck `uploading`/`error` is
 *  simply skipped (the user sees why on the chip itself and can remove/retry). */
interface PendingAttachment {
  /** Stable id independent of array position — the async upload callback uses this to patch the
   *  right entry even if other items were added/removed/reordered while it was in flight. */
  localId: string;
  file: File;
  name: string;
  size: string;
  kind: MediaKind;
  source: PendingAttachmentSource;
  /** Local objectURL for inline preview of voice recordings — revoked on removal/send. */
  previewUrl?: string;
  status: PendingAttachmentStatus;
  /** Set once the upload succeeds — this (not a re-upload) is what send() attaches. */
  fileId?: string;
  /** Server-confirmed content type (may differ from `file.type` for a transcoded voice note). */
  contentType?: string;
  /** Friendly message shown inline on the chip when status is `error`. */
  errorMessage?: string;
}

function nextLocalId(): string {
  return crypto.randomUUID();
}

const MEDIA_KIND_ICON: Record<MediaKind, string> = {
  image: 'heroPhoto',
  video: 'heroVideoCamera',
  audio: 'heroMusicalNote',
  document: 'heroDocumentText',
  // A human never uploads a sticker via the file picker (stickers only arrive inbound from
  // WhatsApp) — kept for Record completeness, reusing the photo icon.
  sticker: 'heroPhoto',
};

/** Short PT-BR name for a MIME, used to render the allowed-formats list in the upload error. */
const MIME_SHORT_NAME: Record<string, string> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'video/mp4': 'MP4',
  'audio/ogg': 'OGG',
  'application/pdf': 'PDF',
};

function shortMimeName(mime: string): string {
  return MIME_SHORT_NAME[mime] ?? mime;
}

/** PT-BR label for the media kind the rejected MIME belongs to — scopes the message to "for
 *  images" rather than reading as a blanket claim about the whole channel (a channel accepting
 *  PDF documents but not WEBP images is not a contradiction; the unscoped wording read like one). */
const MEDIA_KIND_LABEL: Record<string, string> = {
  image: 'imagens',
  video: 'vídeos',
  audio: 'áudios',
  document: 'documentos',
  sticker: 'figurinhas',
};

/** Reads the backend's `media_mime_not_allowed` error (thrown at attach time, before the upload
 *  URL is even issued) into a friendly PT-BR message scoped to the rejected attachment's kind and
 *  naming the accepted formats for THAT kind — falls back to a generic message for any other
 *  upload failure (network error, size limit, etc). */
function describeUploadError(err: HttpErrorResponse, filename: string): { title: string; description: string } {
  const body = err.error as { error?: string; details?: { kind?: string; allowed?: string[] } } | null;
  if (body?.error === 'media_mime_not_allowed' && body.details?.allowed?.length) {
    const allowed = body.details.allowed.map(shortMimeName).join(', ');
    const kindLabel = MEDIA_KIND_LABEL[body.details.kind ?? ''] ?? 'este tipo de anexo';
    return { title: 'Formato não aceito', description: `Para ${kindLabel}, este canal só aceita: ${allowed}.` };
  }
  return { title: 'Falha no envio', description: `Não foi possível enviar ${filename}.` };
}

/** Reply composer: text/note, file attachments, and voice notes, with an icon toolbar. */
@Component({
  selector: 'app-message-composer',
  imports: [
    NgIcon,
    UiTextarea,
    UiBadge,
    UiButton,
    UiFileInput,
    UiIconButton,
    UiPanelCard,
    IfPermissionDirective,
    AudioPlayerComponent,
    TemplatePickerDialogComponent,
  ],
  providers: [
    provideIcons({
      heroDocumentText,
      heroLockClosed,
      heroMicrophone,
      heroMusicalNote,
      heroPaperAirplane,
      heroPaperClip,
      heroPencilSquare,
      heroPhoto,
      heroSparkles,
      heroStop,
      heroVideoCamera,
      heroXMark,
    }),
  ],
  templateUrl: './message-composer.html',
  styleUrl: './message-composer.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageComposerComponent {
  protected readonly store = inject(ConversationsStore);
  protected readonly recorder = inject(VoiceRecorderService);
  private readonly uploads = inject(FileUploadService);
  private readonly toast = inject(ToastrService);

  protected readonly text = signal('');
  protected readonly noteMode = signal(false);
  protected readonly pending = signal<readonly PendingAttachment[]>([]);
  protected readonly canRecord = this.recorder.isSupported;

  /** True while at least one queued attachment is still uploading — blocks Send (an item must
   *  finish, successfully or not, before the message can go out). Upload starts on attach, not
   *  on send, so a rejected format surfaces immediately rather than after clicking Send. */
  protected readonly isUploading = computed(() => this.pending().some((item) => item.status === 'uploading'));

  /** The send button is enabled when there is text or at least one queued attachment. */
  protected readonly hasContent = computed(() => this.text().trim().length > 0 || this.pending().length > 0);

  /** True quando o canal (número) da conversa está desativado em Integrações › Canais.
   *  Bloqueia todo envio (nem template) — nem chega ao provedor. Notas seguem liberadas. */
  protected readonly channelInactive = computed(() => this.store.selectedConversationChannelActive() === false);

  /** Reply window has closed for the selected conversation — outbound replies are blocked,
   *  but internal notes (note mode) stay allowed regardless of channel. Canal inativo também
   *  bloqueia (com aviso próprio no banner). */
  protected readonly replyBlocked = computed(
    () => !this.noteMode() && (this.channelInactive() || this.store.replyWindowExpired()),
  );

  /** True when the channel can reopen the closed window with an approved template (WhatsApp).
   *  Drives the banner copy/CTA — template send stays usable even while text/media are blocked.
   *  Canal inativo não pode enviar nem template, então some o CTA. */
  protected readonly canReopenWithTemplate = computed(
    () => !this.channelInactive() && this.store.replyWindowReopenWith() === 'template',
  );

  /** Whether any approved templates exist for the selected conversation's channel. Lazy-loads
   *  on first selection. Used to surface the template button only on channels that have them. */
  protected readonly hasTemplates = computed(() => this.store.templatesForSelectedChannel().length > 0);

  protected readonly templatePickerOpen = signal(false);

  private readonly conversation = computed(() => this.store.selectedConversation());

  constructor() {
    // Lazy-load templates whenever the selected conversation changes — keeps the button
    // visibility honest without forcing the inbox to preload templates for every channel.
    effect(() => {
      const conversation = this.conversation();
      if (conversation) this.store.loadTemplatesForSelectedChannel();
    });
    // Auto-open the picker when a send was just rejected with 422 reply_window_expired:
    // pulls the user directly into the reopen flow instead of forcing a manual click.
    effect(() => {
      const target = this.store.replyWindowExpiredConversationId();
      const current = this.conversation()?.id;
      if (target && target === current && this.canReopenWithTemplate()) {
        this.templatePickerOpen.set(true);
        this.store.consumeReplyWindowExpired();
      }
    });
    // Revoke any leftover objectURLs when the composer is destroyed (conversation switch,
    // route change). Items removed during normal use are revoked in removePendingFile/
    // clearPending; this catches the destroy path.
    inject(DestroyRef).onDestroy(() => {
      for (const item of this.pending()) {
        if (item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      }
    });
  }

  protected iconFor(kind: MediaKind): string {
    return MEDIA_KIND_ICON[kind];
  }

  setText(value: string): void {
    this.text.set(value);
  }

  /** WhatsApp-style: Enter sends, Shift+Enter inserts a newline. Ignores IME composition. */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  toggleNoteMode(): void {
    this.noteMode.update((on) => !on);
  }

  openTemplatePicker(): void {
    this.templatePickerOpen.set(true);
  }

  closeTemplatePicker(): void {
    this.templatePickerOpen.set(false);
  }

  onTemplateSubmit(submit: TemplatePickerSubmit): void {
    this.store.sendTemplateMessage({
      templateId: submit.templateId,
      templateLanguage: submit.templateLanguage,
      ...(submit.templateComponents.length > 0 && { templateComponents: submit.templateComponents }),
      contentText: submit.contentText,
    });
    this.templatePickerOpen.set(false);
  }

  send(): void {
    const body = this.text().trim();
    const queued = this.pending();

    // Outbound replies are blocked outside Instagram's 24h window; notes still flow.
    if (this.replyBlocked()) {
      return;
    }
    // The Send button is already disabled while uploading; this guards Enter (onKeydown calls
    // send() directly, bypassing the button's [disabled]) from firing mid-upload.
    if (this.isUploading()) {
      return;
    }

    if (this.noteMode()) {
      // Notes are text-only; any queued attachments are ignored in note mode.
      if (body.length > 0) {
        this.store.createNote(body);
        this.text.set('');
      }
      // Anexos enfileirados NÃO são perdidos — mas antes ficavam retidos sem nenhum feedback.
      if (queued.length > 0) {
        this.toast.info('Anexos ficam para depois', 'A nota é só texto — os anexos continuam na fila para a próxima mensagem.');
      }
      return;
    }

    if (queued.length > 0) {
      this.sendQueuedAttachments(queued, body || undefined);
      this.text.set('');
      return;
    }

    if (body.length > 0) {
      this.store.sendMessage(body);
      this.text.set('');
    }
  }

  /** Dispatches each already-uploaded attachment; errored/still-uploading items are kept in the
   *  queue (the chip already shows why) instead of vanishing as if handled. */
  private sendQueuedAttachments(queued: readonly PendingAttachment[], caption: string | undefined): void {
    const leftBehind: PendingAttachment[] = [];
    for (const item of queued) {
      if (item.status === 'uploaded' && item.fileId) {
        this.store.sendAttachmentMessage({
          fileId: item.fileId,
          mediaKind: contentTypeToMediaKind(item.contentType ?? item.file.type),
          filename: item.name,
          caption,
          ...(item.source === 'voice' && { mediaProfile: 'voice-note' }),
        });
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      } else {
        leftBehind.push(item);
      }
    }
    this.pending.set(leftBehind);
  }

  /** Queue selected files and start uploading each immediately — a rejected format (or any other
   *  upload failure) surfaces right on the chip as soon as it's attached, not after Send. */
  onFilesSelected(input: HTMLInputElement): void {
    const files = Array.from(input.files ?? []);
    input.value = '';
    const conversation = this.conversation();
    if (!conversation || files.length === 0) {
      return;
    }
    const channelKind = channelKindFromLabel(conversation.channel);

    for (const file of files) {
      if (exceedsSizeLimit(file.size, file.type, channelKind)) {
        this.toast.error('Anexo muito grande', `${file.name} excede ${formatBytes(maxUploadBytes(file.type, channelKind))}.`);
        continue;
      }
      this.queueAndUpload(file, 'file', channelKind);
    }
  }

  removePendingFile(index: number): void {
    this.pending.update((current) => {
      const removed = current[index];
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      return current.filter((_, i) => i !== index);
    });
  }

  /** Click the mic: starts a recording; clicking again stops, queues the result with an inline
   * audio preview, and starts uploading it immediately (same as a paperclip attachment). */
  toggleRecording(): void {
    if (this.recorder.isRecording()) {
      void this.recorder.stop().then((file) => {
        if (!file) return;
        const conversation = this.conversation();
        const channelKind = conversation ? channelKindFromLabel(conversation.channel) : 'webchat';
        this.queueAndUpload(file, 'voice', channelKind, URL.createObjectURL(file));
      });
      return;
    }
    this.recorder.start().then((started) => {
      if (!started) {
        this.toast.error('Gravação indisponível', 'Permita o microfone ou use um navegador compatível.');
      }
    });
  }

  protected quotedSenderLabel(sender: ThreadMessage['sender']): string {
    return quotedReplySenderLabel(sender, this.conversation()?.contact);
  }

  /** Queues the file as a pending chip and starts uploading it right away — the whole point being
   *  that a rejected format (media_mime_not_allowed) surfaces at attach time, not at send time. */
  private queueAndUpload(file: File, source: PendingAttachmentSource, channelKind: string, previewUrl?: string): void {
    const localId = nextLocalId();
    this.pending.update((current) => [
      ...current,
      {
        localId,
        file,
        name: file.name,
        size: formatBytes(file.size),
        kind: contentTypeToMediaKind(file.type),
        source,
        status: 'uploading',
        ...(previewUrl && { previewUrl }),
      },
    ]);

    this.uploads
      .upload(file, {
        channelKind,
        // Voice notes get renormalized server-side: the API rewrites the S3 object to mono
        // OGG/Opus (PTT profile) so both the recipient on WhatsApp and the operator downloading
        // the file get a universally playable asset.
        ...(source === 'voice' && { transcodeProfile: 'voice-note-ogg' }),
      })
      .subscribe({
        next: (uploaded) => {
          this.pending.update((current) => current.map((item) => (
            item.localId === localId
              ? { ...item, status: 'uploaded', fileId: uploaded.fileId, contentType: uploaded.contentType }
              : item
          )));
        },
        error: (err: HttpErrorResponse) => {
          const { title, description } = describeUploadError(err, file.name);
          this.pending.update((current) => current.map((item) => (
            item.localId === localId ? { ...item, status: 'error', errorMessage: description } : item
          )));
          this.toast.error(title, description);
        },
      });
  }
}
